/* ══════════════════════════════════════════════════════════════════════════════════════════════════
   MNL — GARDE-FOU DE LA CONVERSION DE DOSE SUR LA PAGE QUI FACTURE. V1260 — 06/08/2026.
   ══════════════════════════════════════════════════════════════════════════════════════════════════

   ── LE DÉFAUT QU'IL CORRIGE : LE REPLI ÉTAIT À L'ENVERS DU RISQUE ─────────────────────────────────
   Quatre surfaces lisent MNL_DOSES. Trois d'entre elles — bilan.html, quiz.html, recharge.html —
   REFUSENT DE DÉMARRER si le module manque : elles lèvent une exception, la page ne fait rien, et
   personne n'est facturé de travers. La quatrième, le CONFIGURATEUR — celle qui calcule le prix,
   choisit la variante et remplit le panier —, se contentait d'un `console.error` et continuait :
   les doses anciennes ressortaient BRUTES puis écrêtées au plafond du jour. 36 doses changent, et
   elles changent DANS LE MAUVAIS SENS : l'écrêtage fait payer un cran de PLUS pour une dose de MOINS
   (une taurine v4 de 1500 mg devient 1000 mg = l'indice 5 au lieu de l'indice 4).
   Le plus permissif était le seul à toucher à l'argent. C'est cette asymétrie qu'on inverse.

   ── POURQUOI PAS « REFUSER DE DÉMARRER », COMME LES TROIS AUTRES ──────────────────────────────────
   Parce que sur la page produit, « refuser de démarrer » veut dire « ne plus rien vendre ». Et ce
   serait une réponse disproportionnée, pour une raison vérifiable : LE MODULE NE SERT À RIEN DANS UNE
   VENTE NEUVE. Le prix ne sort pas de MNL_DOSES, il sort de l'INDICE dans PALIERS (mnlPalierIndexOf →
   PRICE_GRID), qui vit dans le moteur. MNL_DOSES n'est appelé qu'en DEUX endroits du moteur, et les
   deux ne servent qu'à relire une formule ANCIENNE :
       mnlGridIdxToCurrent  ← decodeCompact, c.-à-d. le QR ?r= d'une étiquette
       mnlMigrateLegacyDose ← clampLoadedDoses (?f=) et mnlApplyDoses (communauté / avis)
   Un visiteur qui compose sa formule de zéro ne passe par aucun des deux. Bloquer sa commande le
   punirait d'une panne de déploiement qui ne le concerne pas.

   ── LA RÈGLE RETENUE, ET C'EST TOUTE LA PASSE EN UNE PHRASE ───────────────────────────────────────
       ON NE REFUSE PAS LA VENTE. ON REFUSE DE RELIRE UNE FORMULE ANCIENNE QU'ON NE SAIT PLUS
       CONVERTIR — et on le DIT au client, au lieu de lui vendre une formule silencieusement fausse.
   Concrètement, quand le module manque ou que son autotest échoue :
     · les charges utiles ANCIENNES sont neutralisées AVANT que le moteur ne les lise (?r=, ?f=, et
       l'entrée « formules de la communauté / avis » mnlApplyDoses) ;
     · un bandeau VISIBLE explique au client que sa formule n'a pas pu être relue, avec un bouton
       « Réessayer » — pas un console.error que personne ne lit ;
     · le configurateur démarre vierge : composer, choisir, payer, tout marche.
   Le client perd un préremplissage. Il ne perd pas d'argent, et il n'apprend pas trois semaines plus
   tard que son pot ne contient pas ce qu'il a payé.

   ── LE SECOND CONTRÔLE, QUI N'EXISTAIT NULLE PART : LE MODULE EST LÀ MAIS FAUX ────────────────────
   « Absent » n'est pas le seul mode de panne, ni le plus probable. Le mode de panne HISTORIQUE de ce
   dépôt, c'est « présent, chargé, et pas d'accord avec le moteur » : la V1171 a réécrit une archive
   dans le configurateur sans toucher au reste, et rien n'a crié. Ce garde-fou compare donc, à chaque
   chargement de page, les tables du MOTEUR (window.MNL_PALIERS / _V2 / _LEGACY / _V3 / _V4) à celles
   du MODULE. Une divergence est traitée exactement comme une absence.
   V1260 : ce contrôle échouait OUVERT quand le moteur ne publiait pas ses tables — voir §2.

   ── ORDRE DE CHARGEMENT — MESURÉ, ET PAS CE QUE DISAIT LA V1259 ───────────────────────────────────
   Ce fichier est le TROISIÈME `defer` de la page, APRÈS le module ET après le moteur :
       mnl-dose-conversion.js → preworkout-configurator-v2.js → mnl-dose-guard.js
   La spec HTML garantit l'ordre d'exécution des `defer` et qu'ils s'exécutent tous avant
   DOMContentLoaded. Jusque-là, c'est vrai.

   ⚠ MAIS LA CONCLUSION QU'EN TIRAIT LA V1259 EST FAUSSE, ET C'EST MESURÉ AU BANC (navigateur réel,
     fichiers réels). Elle affirmait : « le moteur ne lit l'URL qu'au DOMContentLoaded (init →
     loadFromUrl), donc ce garde-fou travaille après que le moteur a publié ses tables et AVANT qu'il
     n'ait lu la moindre charge utile ». Il n'y a pas de telle fenêtre.
     Raison : quand un script `defer` s'exécute, `document.readyState` vaut DÉJÀ « interactive » (la
     spec passe le document à « interactive » AVANT de jouer les `defer`). Or la ligne 4323 du
     configurateur dit `if (document.readyState === 'loading') …addEventListener… else init();` :
     c'est donc la branche `else` qui est prise, et `init()` s'exécute SYNCHRONEMENT pendant la
     balise du moteur. Quand ce fichier démarre, `bindEvents()` a publié les tables ET
     `loadFromUrl()` a déjà décodé `?r=`.
     CONSÉQUENCE HONNÊTE : la comparaison des tables (§2) fonctionne ; la neutralisation de `?r=`/`?f=`
     (§4a) arrive APRÈS la première lecture — elle empêche une relecture, pas la lecture initiale.
     Pour neutraliser vraiment, il faudrait charger ce fichier AVANT le moteur. Le §2 est écrit pour
     le supporter (il piège l'écriture des tables au lieu de les lire une fois), mais le §4b devrait
     alors être repris : il enveloppe `window.mnlApplyDoses`, que le moteur définirait APRÈS nous et
     écraserait. Ce déplacement n'a PAS été fait dans cette passe — il n'était pas dans son périmètre.
   La déplacer en `async` casserait tout, dans tous les cas.

   TÉMOIN POSITIF : `window.MNL_DOSE_GUARD` porte le verdict complet — état, motifs, signature du
   module, charges utiles neutralisées, tables comparées. `MNL_DOSE_GUARD.rapport()` l'imprime.
   ══════════════════════════════════════════════════════════════════════════════════════════════════ */
(function () {
  'use strict';

  var TAG = '[MNL-GARDE]';
  var VERSION = 'V1260';

  var etat = {
    version: VERSION,
    ok: null,                 /* true = module sain ET moteur d'accord */
    motifs: [],               /* pourquoi ce n'est pas ok */
    signature: null,
    empreinte: null,
    autotest: null,
    tables: [],               /* résultat de la comparaison moteur ↔ module */
    neutralise: [],           /* ce qui a été refusé, avec sa valeur d'origine */
    urlOrigine: null
  };

  /* V1260 : déclarée ICI, et pas à côté de degrader(). Le §2 peut dégrader SYNCHRONEMENT (table
     divergente lue immédiatement) ; une `var degradeFait = false` placée plus bas s'exécuterait
     APRÈS ce premier appel et remettrait le drapeau à zéro, si bien qu'un piège qui se déclenche
     ensuite rejouerait toute la neutralisation. Un garde-fou ne doit pas crier deux fois la même
     panne, ni retirer deux fois la même charge utile. */
  var degradeFait = false;

  function crier(msg, extra) {
    try { console.error(TAG + ' ' + msg, extra === undefined ? '' : extra); } catch (_) {}
  }
  function noter(msg, extra) {
    try { console.info(TAG + ' ' + msg, extra === undefined ? '' : extra); } catch (_) {}
  }

  /* ── 1. LE MODULE EST-IL LÀ, ET EST-IL LUI-MÊME D'ACCORD AVEC LUI-MÊME ? ───────────────────────── */
  var D = null;
  try { D = window.MNL_DOSES || null; } catch (_) {}

  if (!D || typeof D.fromMg !== 'function' || typeof D.fromIndex !== 'function') {
    etat.motifs.push('mnl-dose-conversion.js absent ou incomplet (window.MNL_DOSES introuvable)');
  } else {
    etat.signature = D.SIGNATURE || null;
    etat.empreinte = D.FINGERPRINT || null;
    var diag = null;
    try { diag = (typeof D.verifier === 'function') ? D.verifier() : null; } catch (e) { etat.motifs.push('verifier() a levé : ' + (e && e.message)); }
    if (diag) {
      etat.autotest = diag.autotest;
      if (!diag.ok) etat.motifs = etat.motifs.concat(diag.motifs);
    } else if (typeof D.selfTest === 'function') {
      var st = D.selfTest();
      etat.autotest = st;
      if (!st.ok) etat.motifs.push('autotest du module en échec (' + st.echecs.length + '/' + st.total + ')');
    } else {
      etat.motifs.push('module trop ancien : ni verifier() ni selfTest() (déploiement partiel ?)');
    }
  }

  /* ── 2. LE MOTEUR EST-IL D'ACCORD AVEC LE MODULE ? (le mode de panne historique) ───────────────── */
  /* Les archives sont des PHOTOGRAPHIES de pots imprimés. Deux photographies du même pot qui ne se
     ressemblent pas, c'est un pot qu'on ne sait plus lire — et la V1171 a fait exactement ça.

     ── V1260 : CE CONTRÔLE ÉCHOUAIT OUVERT, ET PAS OÙ ON LE CROYAIT ─────────────────────────────
     Mesuré au banc (navigateur réel, fichiers réels, pas une lecture de code) :
       · PAGE SAINE — les cinq tables SONT publiées avant que ce fichier ne s'exécute, et la
         comparaison donnait bien « identique » ×5. Le contrôle marchait.
       · PAGE OÙ LE MOTEUR CASSE — les tables ne sont JAMAIS publiées : `window.MNL_PALIERS*` reste
         `undefined`, chaque table était classée « non comparée », AUCUN motif n'était ajouté, et le
         garde-fou concluait `ok: true` en imprimant « 0 tables du moteur conformes ». Vert sans
         avoir rien regardé.
     Autrement dit : le contrôle se taisait EXACTEMENT dans le cas où il sert. « Je n'ai pas pu
     vérifier » se lisait comme « tout va bien » — la définition même d'un garde qui échoue ouvert.

     ── POURQUOI LES TABLES PEUVENT MANQUER ICI ──────────────────────────────────────────────────
     Elles ne sont pas déclarées au niveau du fichier : `PALIERS` / `_V4` / `_V3` / `_V2` /
     `_LEGACY` vivent DANS `bindEvents()` (preworkout-configurator-v2.js l.2759) et ne sont exposées
     sur `window` qu'à la ligne 2913, c.-à-d. quand `init()` appelle `bindEvents()`. Si `init()`
     lève avant cet appel — markup incomplet, section à moitié rendue, déploiement partiel : le
     genre de panne que ce fichier existe pour attraper —, rien n'est publié, jamais.

     ── LA RÈGLE, DÉSORMAIS ──────────────────────────────────────────────────────────────────────
     UNE TABLE NON COMPARÉE EST UN DÉFAUT, PAS UN SILENCE. Trois états seulement, et deux sont des
     échecs : « identique », « DIVERGENTE », « JAMAIS PUBLIÉE ». Et pour ne pas dépendre de l'ordre
     des balises (qui a déjà changé deux fois), on ne se contente pas de LIRE `window` : on PIÈGE
     l'écriture. Table déjà là → comparaison immédiate ; pas encore là → accesseur qui compare au
     moment où le moteur publie ; toujours rien au `load` → motif. */

  var PAIRES = (D && D.KEYS) ? [
    ['MNL_PALIERS',        D.CURRENT,             'grille COURANTE (elle fixe l\'indice, donc le PRIX)'],
    /* V1271 : PALIERS_V5 rejoint la liste le jour même où elle devient une archive. La règle de ce
       bloc — UNE TABLE NON COMPARÉE EST UN DÉFAUT — n'a de valeur que si la liste suit les archives ;
       l'oublier ici, c'était le défaut que la V1260 a corrigé pour V3 et V4, un cran plus tard. */
    ['MNL_PALIERS_V5',     D.GRIDS && D.GRIDS[5], 'archive v5 — décodeur des pots marqués 8/9 (tyrosine 2 g)'],
    ['MNL_PALIERS_V4',     D.GRIDS && D.GRIDS[4], 'archive v4 — décodeur des pots v4 imprimés'],
    ['MNL_PALIERS_V3',     D.GRIDS && D.GRIDS[3], 'archive v3 — décodeur des pots v3 imprimés'],
    ['MNL_PALIERS_V2',     D.GRIDS && D.GRIDS[2], 'archive v2 — décodeur des pots v2 imprimés'],
    ['MNL_PALIERS_LEGACY', D.GRIDS && D.GRIDS[1], 'archive v1 — décodeur des pots v1 imprimés']
  ] : [];

  var enAttente = {};   /* nom -> true tant que le moteur n'a pas publié la table */

  /* Compare UNE table et enregistre son verdict. Rend true si tout va bien. */
  function comparerTable(nom, moteur, ref, quoi) {
    var ecarts = [], compares = 0, i, k;
    for (i = 0; i < D.KEYS.length; i++) {
      k = D.KEYS[i];
      if (!moteur[k] || !ref[k]) continue;      /* colonne absente d'un côté : la bétaïne, notamment */
      compares++;
      if (moteur[k].join(',') !== ref[k].join(',')) {
        ecarts.push(k + ' : moteur [' + moteur[k] + '] ≠ module [' + ref[k] + ']');
      }
    }
    /* Zéro colonne comparée = le même échec ouvert, un cran plus bas : une table présente mais vide,
       ou dont toutes les colonnes ont été renommées, passait pour « identique ». */
    if (!compares) {
      inscrire(nom, 'VIDE', quoi, null);
      degrader([nom + ' du moteur ne partage AUCUNE colonne avec le module (' + quoi + ') — table vide ou clés renommées : le miroir n\'est pas vérifié.']);
      return false;
    }
    if (ecarts.length) {
      inscrire(nom, 'DIVERGENTE', quoi, ecarts);
      degrader([nom + ' du moteur diverge du module (' + quoi + ') : ' + ecarts.join(' · ')]);
      return false;
    }
    inscrire(nom, 'identique', quoi, null, compares);
    return true;
  }

  function inscrire(nom, etatTable, quoi, ecarts, compares) {
    for (var i = 0; i < etat.tables.length; i++) {
      if (etat.tables[i].table === nom) { etat.tables.splice(i, 1); break; }
    }
    var ligne = { table: nom, etat: etatTable, quoi: quoi };
    if (ecarts) ligne.ecarts = ecarts;
    if (compares) ligne.colonnes = compares;
    etat.tables.push(ligne);
  }

  for (var p = 0; p < PAIRES.length; p++) {
    (function (nom, ref, quoi) {
      if (!ref) {
        inscrire(nom, 'SANS RÉFÉRENCE', quoi, null);
        etat.motifs.push('le module n\'expose aucune grille de référence pour ' + nom + ' (' + quoi + ')');
        return;
      }
      var dejaLa = null;
      try { dejaLa = window[nom] || null; } catch (_) {}
      if (dejaLa) { comparerTable(nom, dejaLa, ref, quoi); return; }

      /* Pas encore publiée : on piège l'écriture. L'accesseur NE DOIT JAMAIS LEVER — le moteur
         publie ses cinq tables dans un unique `try { … } catch(_){}` (l.2913) : une exception ici
         avalerait silencieusement les publications suivantes. */
      enAttente[nom] = true;
      inscrire(nom, 'en attente', quoi, null);
      try {
        Object.defineProperty(window, nom, {
          configurable: true,
          enumerable: true,
          get: function () { return undefined; },
          set: function (v) {
            try {
              /* On repose d'abord une propriété NORMALE : le moteur relit ces tables en
                 permanence (decodeCompact → window.MNL_PALIERS_V3/_V4), on ne laisse pas un
                 accesseur sur son chemin. */
              Object.defineProperty(window, nom, { value: v, writable: true, configurable: true, enumerable: true });
            } catch (_) { try { delete window[nom]; window[nom] = v; } catch (__) {} }
            try {
              delete enAttente[nom];
              if (v) comparerTable(nom, v, ref, quoi);
              else degrader([nom + ' publiée VIDE par le moteur (' + quoi + ')']);
            } catch (_) {}
          }
        });
      } catch (_) {
        delete enAttente[nom];
        inscrire(nom, 'PIÈGE IMPOSSIBLE', quoi, null);
        etat.motifs.push('impossible de surveiller ' + nom + ' (defineProperty refusé)');
      }
    }(PAIRES[p][0], PAIRES[p][1], PAIRES[p][2]));
  }

  etat.ok = etat.motifs.length === 0;

  /* ── 3. TOUT VA BIEN : ON LE DIT UNE FOIS, ET ON NE TOUCHE À RIEN ──────────────────────────────── */
  /* V1260 : le compte des tables est désormais DÉTAILLÉ. L'ancien message disait « N tables du moteur
     conformes » sans jamais dire combien étaient ATTENDUES — c'est ainsi que « 0 tables du moteur
     conformes » a pu s'imprimer juste après le mot « OK » sans alerter personne. */
  function compteTables(e) { return etat.tables.filter(function (t) { return t.etat === e; }).length; }
  if (etat.ok) {
    noter('conversion de dose OK — ' + etat.signature + ' · ' + etat.empreinte +
          ' · autotest ' + (etat.autotest ? (etat.autotest.total - etat.autotest.echecs.length) + '/' + etat.autotest.total : '?') +
          ' · tables ' + compteTables('identique') + '/' + PAIRES.length + ' conformes' +
          (compteTables('en attente') ? ' · ' + compteTables('en attente') + ' EN ATTENTE de publication par le moteur (verdict au load)' : ''));
  } else { degrader(null); }

  /* ── 4. ÇA NE VA PAS : ON NEUTRALISE LES CHARGES UTILES ANCIENNES ──────────────────────────────── */
  /* V1260 : c'était un bloc `else` joué UNE FOIS, au chargement. Ça ne pouvait pas convenir à un
     contrôle qui, désormais, peut conclure PLUS TARD (une table comparée au moment où le moteur la
     publie). `degrader()` est donc appelable à tout instant et IDEMPOTENTE. */
  /* (`degradeFait` est déclarée tout en haut, AVANT le §2 : celui-ci peut appeler degrader() de
     façon synchrone, et une `var` déclarée ici la remettrait à false juste après — ce qui rejouerait
     toute la neutralisation au prochain appel.) */
  function degrader(nouveauxMotifs) {
    try {
      if (nouveauxMotifs && nouveauxMotifs.length) {
        for (var i = 0; i < nouveauxMotifs.length; i++) {
          if (etat.motifs.indexOf(nouveauxMotifs[i]) < 0) etat.motifs.push(nouveauxMotifs[i]);
        }
      }
      etat.ok = false;
      if (degradeFait) { if (nouveauxMotifs) nouveauxMotifs.forEach(function (m) { crier('  motif (tardif) : ' + m); }); return; }
      degradeFait = true;

      crier('CONVERSION DE DOSE INDISPONIBLE OU INCOHÉRENTE — aucune formule ANCIENNE ne sera relue sur cette page.');
      etat.motifs.forEach(function (m) { crier('  motif : ' + m); });
      crier('  conséquence : ?r= / ?f= / « formules de la communauté » sont refusés. La composition et la commande NEUVES restent normales.');
      crier('  à faire : redéployer LES DEUX exemplaires du module (thème Shopify ET Vercel), puis « npm run verify ».');

      /* 4a. ?r= (QR d'étiquette) et ?f= (partage, Coach, e-mail) — retirés de l'URL.
             ⚠ V1260 — CE QUE CE RETRAIT NE FAIT PAS, ET C'EST MESURÉ : il n'arrive PAS avant que le
             moteur n'ait lu l'URL. La version V1259 affirmait le contraire (« le moteur ne lit l'URL
             qu'au DOMContentLoaded ») ; c'est FAUX. Un script `defer` s'exécute alors que
             `document.readyState` vaut déjà « interactive », donc la ligne 4323 du configurateur
             (`if (readyState === 'loading') … else init();`) prend la branche `else` et appelle
             `init()` SYNCHRONEMENT, pendant sa propre balise — donc `loadFromUrl()` a déjà décodé
             `?r=` quand ce fichier démarre. Vérifié au banc : « ?r= encore dans l'URL ? OUI ».
             Ce retrait garde deux utilités réelles : il empêche une RELECTURE (retour arrière,
             rechargement, navigation interne) et il évite que l'URL fausse soit partagée. Mais la
             première lecture, elle, a eu lieu. La corriger vraiment demande de charger ce garde-fou
             AVANT le moteur — voir la note d'ordre en tête de fichier. */
      var u = new URL(window.location.href);
      etat.urlOrigine = u.href;
      var pris = [];
      ['r', 'f'].forEach(function (nom) {
        var v = u.searchParams.get(nom);
        if (v) { pris.push({ param: nom, valeur: v }); u.searchParams.delete(nom); }
      });
      if (pris.length) {
        etat.neutralise = etat.neutralise.concat(pris);
        try { window.history.replaceState(null, '', u.pathname + (u.search ? u.search : '') + u.hash); } catch (_) {}
        crier('  charge utile ancienne retirée de l\'URL : ' + pris.map(function (x) { return '?' + x.param + '=' + x.valeur; }).join(' '));
        crier('  ⚠ le moteur avait déjà lu l\'URL : ce retrait empêche une RELECTURE, il n\'annule pas la première.');
      }
    } catch (_) {}

    /* 4b. mnlApplyDoses — la porte des « formules de la communauté » et du « charger la formule » d'un
           avis. Ces doses viennent de VRAIES COMMANDES décodées côté serveur : ce sont, par
           construction, les formules les plus anciennes qui entrent côté client. Sans conversion,
           elles seraient écrêtées, donc surfacturées. On les refuse en rendant `false`, valeur que
           l'appelant sait déjà lire (c'est ce que la fonction rend quand la charge est vide).
           Celle-ci, au moins, est TOUJOURS prise à temps : elle n'est appelée que sur un clic. */
    try {
      var origApply = window.mnlApplyDoses;
      if (typeof origApply === 'function' && !origApply.__mnlGarde) {
        window.mnlApplyDoses = function (f) {
          if (f && f.doses && typeof f.doses === 'object') {
            etat.neutralise.push({ param: 'mnlApplyDoses', valeur: f.doses });
            crier('  formule de la communauté REFUSÉE (doses non convertibles) :', f.doses);
            bandeau();
            return false;
          }
          return origApply.apply(this, arguments);
        };
        try { window.mnlApplyDoses.__mnlGarde = VERSION; } catch (_) {}
      }
    } catch (_) {}

    /* 4c. Le bandeau. Il ne s'affiche QUE si le client attendait vraiment une ancienne formule —
           sinon on ne va pas inquiéter quelqu'un qui vient simplement composer la sienne. */
    try { if (etat.neutralise.length) { quandPret(bandeau); } } catch (_) {}
  }

  /* ── 4bis. LE CHIEN DE GARDE : « JAMAIS PUBLIÉE » EST UN VERDICT, PAS UN BLANC ──────────────────── */
  /* Si, la page entièrement chargée, une table n'a toujours pas été publiée, c'est que `init()` n'a
     jamais atteint `bindEvents()`. Le moteur est cassé — exactement la panne que ce fichier existe
     pour voir. Avant la V1260 ce cas rendait « ok: true » avec « 0 tables conformes ». */
  function verdictTablesManquantes() {
    var restantes = [];
    for (var nom in enAttente) { if (Object.prototype.hasOwnProperty.call(enAttente, nom)) restantes.push(nom); }
    if (!restantes.length) return;
    restantes.forEach(function (nom) {
      delete enAttente[nom];
      for (var i = 0; i < etat.tables.length; i++) {
        if (etat.tables[i].table === nom) { etat.tables[i].etat = 'JAMAIS PUBLIÉE'; break; }
      }
    });
    degrader(['le moteur n\'a JAMAIS publié ' + restantes.join(', ') +
              ' — init() n\'a pas atteint bindEvents() (configurateur cassé ou déploiement partiel). ' +
              'Les archives des pots imprimés n\'ont donc PAS pu être vérifiées.']);
  }
  try {
    if (document.readyState === 'complete') { setTimeout(verdictTablesManquantes, 0); }
    else { window.addEventListener('load', function () { setTimeout(verdictTablesManquantes, 0); }, { once: true }); }
  } catch (_) { try { window.addEventListener('load', verdictTablesManquantes); } catch (__) {} }

  /* ── 5. LE BANDEAU — AUTONOME, AUCUNE DÉPENDANCE ───────────────────────────────────────────────── */
  /* Volontairement sans mnlToast ni aucun autre utilitaire du thème : si un fichier n'a pas été
     déployé, on ne fait pas l'hypothèse que les autres l'ont été. HTML + style en ligne, rien d'autre. */
  var bandeauPose = false;
  function bandeau() {
    if (bandeauPose) return;
    bandeauPose = true;
    try {
      var d = document.createElement('div');
      d.id = 'mnl-garde-dose';
      d.setAttribute('role', 'alert');
      d.style.cssText = 'position:fixed;left:0;right:0;bottom:0;z-index:2147483000;background:#1b1b1b;' +
        'color:#fff;padding:16px 18px;font:15px/1.45 -apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;' +
        'box-shadow:0 -6px 24px rgba(0,0,0,.35);display:flex;gap:14px;align-items:flex-start;flex-wrap:wrap';
      var txt = document.createElement('div');
      txt.style.cssText = 'flex:1 1 320px;min-width:0';
      txt.innerHTML =
        '<strong style="display:block;color:#E84E1B;margin-bottom:4px">Nous n\'avons pas pu relire votre ancienne formule</strong>' +
        'Par prudence, nous préférons ne rien préremplir plutôt que d\'afficher — et de vous facturer — des dosages qui ' +
        'pourraient être faux. Vous pouvez composer votre formule normalement, ou réessayer dans un instant.';
      var actions = document.createElement('div');
      actions.style.cssText = 'display:flex;gap:10px;flex:0 0 auto';
      var retry = document.createElement('button');
      retry.type = 'button';
      retry.textContent = 'Réessayer';
      retry.style.cssText = 'min-height:44px;padding:0 18px;border:0;border-radius:8px;background:#E84E1B;color:#fff;font:600 15px/1 inherit;cursor:pointer';
      retry.addEventListener('click', function () {
        try { window.location.href = etat.urlOrigine || window.location.href; } catch (_) { window.location.reload(); }
      });
      var close = document.createElement('button');
      close.type = 'button';
      close.textContent = 'Fermer';
      close.setAttribute('aria-label', 'Fermer le message');
      close.style.cssText = 'min-height:44px;padding:0 18px;border:1px solid rgba(255,255,255,.35);border-radius:8px;background:transparent;color:#fff;font:600 15px/1 inherit;cursor:pointer';
      close.addEventListener('click', function () { try { d.remove(); } catch (_) {} });
      actions.appendChild(retry); actions.appendChild(close);
      d.appendChild(txt); d.appendChild(actions);
      (document.body || document.documentElement).appendChild(d);
    } catch (e) { crier('bandeau impossible à poser', e); }
  }

  function quandPret(fn) {
    if (document.body) { fn(); return; }
    try { document.addEventListener('DOMContentLoaded', fn, { once: true }); } catch (_) { document.addEventListener('DOMContentLoaded', fn); }
  }

  /* ── 6. CONTRÔLE CROISÉ DES DEUX ORIGINES, À LA DEMANDE : ?mnl-verif=1 ─────────────────────────── */
  /* Le module ne peut pas savoir tout seul si l'AUTRE exemplaire lui ressemble : une page ne charge
     qu'une copie. Ce contrôle-ci va chercher la copie Vercel depuis la page Shopify et compare les
     deux SIGNATURE. Il n'est PAS actif par défaut — on n'ajoute pas une requête réseau à une page de
     vente pour un diagnostic. Le fondateur l'active en ouvrant la page produit avec ?mnl-verif=1,
     ou en appelant MNL_DOSE_GUARD.croiser() depuis la console. */
  function croiser(url) {
    var cible = url || 'https://mynutrilab-ai.vercel.app/mnl-dose-conversion.js';
    if (!D) { crier('contrôle croisé impossible : le module local est absent.'); return Promise.resolve({ ok: false, motif: 'module local absent' }); }
    return fetch(cible, { cache: 'no-store' })
      .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.text(); })
      .then(function (txt) {
        /* On n'exécute PAS le texte distant. On recalcule sa signature de la même façon que le module
           calcule la sienne : hachage du texte source de la fabrique, retours chariot retirés. Le
           marqueur de début est le nom de la fabrique, qui est justement là pour ça. */
        var i = txt.indexOf('function MNL_DOSE_MODULE()');
        if (i < 0) throw new Error('la copie distante n\'expose pas la fabrique nommée (version < V1259 ?)');
        var j = txt.lastIndexOf('}));');
        if (j < 0) throw new Error('fin de fabrique introuvable dans la copie distante');
        var src = txt.slice(i, j + 1).replace(/\r/g, '');
        var h1 = 5381, h2 = 52711;
        for (var n = 0; n < src.length; n++) { var c = src.charCodeAt(n); h1 = ((h1 * 33) ^ c) >>> 0; h2 = (((h2 * 31) >>> 0) + c) >>> 0; }
        var sig = 'MNLS-' + ('0000000' + h1.toString(16)).slice(-8) + ('0000000' + h2.toString(16)).slice(-8) + '-' + src.length;
        var identique = (sig === D.SIGNATURE);
        var res = { ok: identique, locale: D.SIGNATURE, distante: sig, url: cible };
        if (identique) noter('contrôle croisé : les deux origines portent la MÊME signature — ' + sig);
        else crier('CONTRÔLE CROISÉ EN ÉCHEC — les deux origines ne portent pas la même signature.\n' +
                   '  Shopify (cette page) : ' + D.SIGNATURE + '\n  Vercel (' + cible + ') : ' + sig +
                   '\n  → un des deux déploiements est en retard. Redéployer, puis « npm run verify ».');
        etat.croise = res;
        return res;
      })
      .catch(function (e) {
        crier('contrôle croisé impossible : ' + (e && e.message));
        etat.croise = { ok: null, motif: String(e && e.message) };
        return etat.croise;
      });
  }

  /* ── 7. TÉMOIN POSITIF ─────────────────────────────────────────────────────────────────────────── */
  function rapport() {
    var l = [];
    l.push('══ GARDE-FOU CONVERSION DE DOSE — ' + VERSION + ' ══');
    l.push('état          : ' + (etat.ok ? 'OK — le configurateur relit les anciennes formules normalement'
                                         : 'DÉGRADÉ — les formules anciennes sont REFUSÉES, la vente neuve reste ouverte'));
    l.push('signature     : ' + (etat.signature || '(module absent)'));
    l.push('empreinte     : ' + (etat.empreinte || '(module absent)'));
    if (etat.autotest) l.push('autotest      : ' + (etat.autotest.total - etat.autotest.echecs.length) + '/' + etat.autotest.total + ' cas');
    /* V1260 — TÉMOIN POSITIF : on imprime le compte ATTENDU, pas seulement les verdicts obtenus.
       « 0 tables conformes » ne doit plus jamais pouvoir se lire comme un succès. */
    l.push('tables moteur : ' + compteTables('identique') + '/' + PAIRES.length + ' conformes' +
           (compteTables('DIVERGENTE') ? ' · ' + compteTables('DIVERGENTE') + ' DIVERGENTE(S)' : '') +
           (compteTables('JAMAIS PUBLIÉE') ? ' · ' + compteTables('JAMAIS PUBLIÉE') + ' JAMAIS PUBLIÉE(S)' : '') +
           (compteTables('en attente') ? ' · ' + compteTables('en attente') + ' en attente' : ''));
    l.push('  détail      : ' + (etat.tables.length ? etat.tables.map(function (t) { return t.table + '=' + t.etat; }).join(' · ') : '(aucune)'));
    if (etat.motifs.length) l.push('motifs        :\n  - ' + etat.motifs.join('\n  - '));
    if (etat.neutralise.length) l.push('neutralisé    : ' + etat.neutralise.map(function (x) { return x.param; }).join(', '));
    if (etat.croise) l.push('contrôle croisé : ' + JSON.stringify(etat.croise));
    l.push('croiser()     : compare cette page à la copie Vercel (ou passer une URL).');
    var s = l.join('\n');
    try { console.log(s); } catch (_) {}
    return s;
  }

  try {
    window.MNL_DOSE_GUARD = {
      VERSION: VERSION,
      etat: etat,
      ok: function () { return etat.ok; },
      rapport: rapport,
      croiser: croiser
    };
  } catch (_) {}

  try {
    if (/[?&]mnl-verif=1\b/.test(window.location.search)) { rapport(); croiser(); }
  } catch (_) {}
}());
